import { Decimal, Numeric } from "decimal.js-light";

Decimal.set({ precision: 20, rounding: Decimal.ROUND_HALF_UP });

export const DECIMAL_CURRENCY_PLACES = 2;
export const DECIMAL_PERCENT_PLACES = 8;
export const DECIMAL_PERCENT_CHANNEL_PLACES = 8;
export const DECIMAL_PERCENT_DISPLAY_PLACES = 2;
export const DECIMAL_PERCENT_TOLERANCE = new Decimal("0.001");
export const DECIMAL_AMOUNT_TOLERANCE = new Decimal("0.000001");

export const toDecimal = (value: unknown): Decimal => {
  if (value instanceof Decimal) return value;
  if (value === null || value === undefined || value === "") return new Decimal(0);
  if (typeof value === "number") {
    return Number.isFinite(value) ? new Decimal(value) : new Decimal(0);
  }
  if (typeof value === "bigint") {
    return new Decimal(value.toString());
  }
  if (typeof value === "string") {
    const normalized = value.replace(/,/g, "").trim();
    if (!normalized) return new Decimal(0);
    try {
      return new Decimal(normalized);
    } catch {
      return new Decimal(0);
    }
  }
  try {
    return new Decimal(value as unknown as Numeric);
  } catch {
    return new Decimal(0);
  }
};

export const decimalToNumber = (value: Decimal): number => value.toNumber();

export const roundCurrency = (value: unknown): Decimal =>
  toDecimal(value).toDecimalPlaces(DECIMAL_CURRENCY_PLACES, Decimal.ROUND_HALF_UP);

export const isAmountWhole = (value: unknown): boolean => {
  const dec = toDecimal(value);
  return dec.minus(dec.toDecimalPlaces(0, Decimal.ROUND_HALF_UP)).abs().lte(DECIMAL_AMOUNT_TOLERANCE);
};

export const roundPercent = (value: unknown, places = DECIMAL_PERCENT_PLACES): Decimal =>
  toDecimal(value).toDecimalPlaces(places, Decimal.ROUND_HALF_UP);

export const amountFromPercent = (base: unknown, percent: unknown): Decimal => {
  const baseDec = toDecimal(base);
  if (baseDec.isZero()) return new Decimal(0);
  return roundCurrency(baseDec.mul(toDecimal(percent)).div(100));
};

export const percentFromAmount = (amount: unknown, base: unknown, places = DECIMAL_PERCENT_PLACES): Decimal => {
  const baseDec = toDecimal(base);
  if (baseDec.isZero()) return new Decimal(0);
  return roundPercent(toDecimal(amount).div(baseDec).mul(100), places);
};

export const hasDecimalChanged = (prev: unknown, next: Decimal): boolean => !toDecimal(prev).equals(next);
